import React, { useState, useEffect } from 'react'
import classNames from 'classnames'
import { Modal, Form, Row, Col } from 'react-bootstrap'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons'
import UserAvatar from 'components/ui/user-avatar/UserAvatar'
import { EnrolState } from 'enum'
import { dateParse } from 'helpers/date.helper'
import Button from 'design-system/button'
import { ButtonSizes, ButtonVariant } from 'design-system/button/src/Button'
import { useWindowSize } from 'hooks/react.hooks'
import { mobileWidth } from 'enum/responsive-width.enum'
import TextAreaInput from 'components/ui/forms/field/text-area/TextArea'
import * as fromActions from 'store/actions/enrols.action'
import { IEnrolModel } from 'models/enrol.model'
import StateEnrol from 'components/ui/state-enrol/StateEnrol'
import { joinKeys } from 'helpers/translations.helper'
import styles from './ModalDetails.module.scss'

interface ICourseProps {
  enrol: IEnrolModel
  isAdmin: boolean
}

const ModalDetails: React.FC<ICourseProps> = ({ enrol, isAdmin }) => {
  const { t } = useTranslation()
  const { course, user } = enrol
  const dispatch = useDispatch()
  const prefix = 'administration.applications.modal'
  const [show, setShow] = useState(false)
  const [noteField, setNoteField] = useState('')
  const handleClose = () => setShow(false)
  const handleShow = () => setShow(true)
  const handleSave = (state: number) => {
    enrol.answerAdmin = noteField
    enrol.stateAdmin = state
    dispatch({ type: fromActions.ENROL_ADMIN_EDIT, payload: enrol })
    setShow(false)
  }

  const handleChange = (event: any) => setNoteField(event.target.value)

  const canRespond = isAdmin && enrol.stateAdmin === EnrolState.Pending

  const [onDesktop, setOnDesktop] = useState(true)
  const windowsSize = useWindowSize()
  useEffect(() => {
    if (windowsSize.width)
      setOnDesktop(windowsSize.width < mobileWidth ? false : true)
  }, [windowsSize])

  const MIN_NOTE = 10
  const disableDenied = noteField.length < MIN_NOTE

  const [showSeeMore, setSeeMore] = useState({
    user: false,
    admin: false,
  } as any)
  const [expandSeeMore, setExpandSeeMore] = useState({
    user: false,
    admin: false,
  } as any)
  const [messages, setMessages] = useState({
    user: `[${t(`${prefix}.noMessage`)}].`,
    admin: `[${t(`${prefix}.noMessage`)}].`,
  } as any)

  useEffect(() => {
    const { note, answerAdmin } = enrol
    note && truncateMessage(note, 'user')
    answerAdmin && truncateMessage(answerAdmin, 'admin')
  }, [enrol])

  const truncateMessage = (message: any, type: string, expanded = false) => {
    let result = message
    if (result.length > 188 && !expanded) {
      const aux = message.substring(0, 188).lastIndexOf(' ')
      result =
        aux > 0 ? message.substring(0, aux) : message.substring(0, 184) + '...'
      setSeeMore((current: any) => {
        return { ...current, [type]: true }
      })
    }
    setMessages((current: any) => {
      return { ...current, [type]: result + ' ' }
    })
  }

  const handleSeeMore = (type: string) => {
    const messages = {
      user: enrol.note,
      admin: enrol.answerAdmin || '',
    } as any
    const currentState = expandSeeMore[type]
    setExpandSeeMore((current: any) => {
      return { ...current, [type]: !currentState }
    })
    truncateMessage(messages[type], type, !currentState)
  }

  const [userArea, setUserArea] = useState<string>()
  useEffect(() => {
    if (enrol.area.data) {
      const {
        area: {
          data: {
            attributes: { name },
          },
        },
      } = enrol
      setUserArea(name)
    }
  }, [enrol])

  return (
    <>
      <Button
        className={`${styles.openModalButton} float-right`}
        size={ButtonSizes.ExtraSmall}
        onClick={handleShow}
      >
        {t('home.myTeam.details')}
      </Button>

      <Modal
        show={show}
        size="lg"
        className={styles.modalContainer}
        onHide={handleClose}
        centered
      >
        <Modal.Header translate="yes" closeButton className="p-0 pb-2">
          <Modal.Title className={styles.modalTitle}>
            {t(`${prefix}.title`)}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className={`p-0 pt-sm-4 ${styles.modalBody}`}>
          <Row>
            <Col xs={12} sm={5}>
              <UserAvatar user={user} className={`${styles.avatar}`}>
                {userArea && (
                  <small
                    className={`grey-darken d-none d-sm-block ${styles.userArea}`}
                  >
                    {userArea}
                  </small>
                )}
              </UserAvatar>
              <p className={`${styles.title} mb-0 mt-3 mt-sm-4`}>
                {t(`${prefix}.course`)}
              </p>
              <p className={`${styles.label} ${styles.courseName} mb-2`}>
                {course.name}
              </p>
              <p className={`${styles.title} my-1`}>
                {t('home.myTeam.table.type')}
              </p>
              <p className={classNames(styles.label)}>
                {t(joinKeys('administration.applications.typeRequests', 'LMS'))}
              </p>
              <p className={`${styles.title} mb-0 mt-1`}>
                {t(`${prefix}.date`)}
              </p>
              <p className={`${styles.label}`}>{dateParse(enrol.createdAt)}</p>
              {onDesktop && course.startDate && (
                <>
                  <p className={`${styles.title} grey-darken mt-1`}>
                    {t(`${prefix}.courseDate`)}
                  </p>
                  <p className={`${styles.label}`}>
                    {t(`${prefix}.courseDates`, {
                      startDate: dateParse(course.startDate),
                      untilDate: dateParse(course.untilDate),
                    })}
                  </p>
                </>
              )}
              {onDesktop && (
                <>
                  <p className={`${styles.title} grey-darken mt-1`}>
                    {t(`${prefix}.note`)}
                  </p>
                  <p className={`${styles.label}`}>
                    {messages.user}
                    {showSeeMore.user && (
                      <a
                        className={styles.seeMore}
                        onClick={() => handleSeeMore('user')}
                      >
                        {t(
                          `${prefix}.see${expandSeeMore.user ? 'Less' : 'More'}`
                        )}
                      </a>
                    )}
                  </p>
                </>
              )}
            </Col>

            <Col xs={12} sm={7} className="pr-sm-5">
              {canRespond ? (
                <Form className="mt-2">
                  <Form.Group className={styles.requestForm}>
                    <Form.Label className={`${styles.formLabel} mb-0`}>
                      {t(`${prefix}.response`)}
                    </Form.Label>
                    <TextAreaInput
                      name={'note'}
                      placeholder={t(`${prefix}.placeholder`)}
                      rows={5}
                      label={''}
                      value={noteField}
                      onChange={handleChange}
                      error={''}
                    />
                    <div className=" mt-2">
                      <FontAwesomeIcon
                        icon={faInfoCircle}
                        className={`${styles.note} pb-0 mr-1 `}
                      ></FontAwesomeIcon>
                      <small className={` ${styles.note}`}>
                        {t(`${prefix}.responseNote`)}
                      </small>
                    </div>
                  </Form.Group>
                </Form>
              ) : (
                <>
                  {onDesktop && <hr />}
                  <div>
                    <p className={`${styles.title} grey-darken mt-1`}>
                      {t(`${prefix}.adminResponseState`)}
                    </p>
                    <StateEnrol
                      className={styles.label}
                      state={enrol.stateAdmin}
                      iconPosition="right"
                    />
                    <p className={`${styles.title} grey-darken mt-3`}>
                      {t(`${prefix}.yourResponse`)}
                    </p>
                    <p className={`${styles.label} mb-1`}>
                      {messages.admin}
                      {showSeeMore.admin && (
                        <a
                          className={styles.seeMore}
                          onClick={() => handleSeeMore('admin')}
                        >
                          {t(
                            `${prefix}.see${
                              expandSeeMore.admin ? 'Less' : 'More'
                            }`
                          )}
                        </a>
                      )}
                    </p>
                  </div>
                </>
              )}
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className="pt-2 pb-0 px-0">
          {canRespond ? (
            <>
              <Button
                size={ButtonSizes.Small}
                variant={ButtonVariant.Danger}
                disabled={disableDenied}
                onClick={() => handleSave(EnrolState.Denied)}
              >
                {disableDenied
                  ? t(`${prefix}.deniedDisabled`, {
                      count: MIN_NOTE - noteField.length,
                    })
                  : t(`${prefix}.denied`)}
              </Button>
              <Button
                size={ButtonSizes.Small}
                variant={ButtonVariant.Success}
                onClick={() => handleSave(EnrolState.Approved)}
              >
                {t(`${prefix}.approved`)}
              </Button>
            </>
          ) : (
            <Button
              size={ButtonSizes.Small}
              variant={ButtonVariant.Secondary}
              onClick={handleClose}
            >
              {t(`${prefix}.close`)}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default ModalDetails
